import { useEffect, useRef, useState } from 'react';

/** 숫자만 받아 YYYY-MM-DD 모양으로(연도 4자리, 월·일 2자리). 예: 20010101 → 2001-01-01 */
export function formatDateDigits(input: string): string {
  const d = input.replace(/\D/g, '').slice(0, 8);
  return [d.slice(0, 4), d.slice(4, 6), d.slice(6, 8)].filter(Boolean).join('-');
}

/** 실제로 있는 날짜인지(2026-02-30 같은 날짜는 거른다) */
export function isRealDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const ms = Date.parse(`${s}T00:00:00Z`);
  return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === s;
}

/**
 * 날짜 칸: 숫자를 치면 YYYY-MM-DD 로 맞춰지고, 달력 버튼으로도 고른다.
 * 올바른 날짜가 다 들어오거나 칸을 비웠을 때만 onChange 를 부른다(중간 입력은 칸에만 남는다).
 */
export function DateField({ id, value, onChange, disabled, required, min = '1900-01-01', max = '2099-12-31', label }: {
  id?: string; value: string; onChange: (v: string) => void; disabled?: boolean; required?: boolean; min?: string; max?: string; label?: string;
}) {
  const [text, setText] = useState(value);
  const [touched, setTouched] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  // 바깥에서 값이 바뀌면(불러오기·달력 선택) 칸도 맞춘다
  useEffect(() => { setText(value); }, [value]);
  const inRange = (s: string) => s >= min && s <= max;
  const invalid = touched && text !== '' && !(isRealDate(text) && inRange(text));
  const commit = (next: string) => {
    setText(next);
    if (next === '') onChange('');
    else if (isRealDate(next) && inRange(next)) onChange(next);
  };
  return (
    <span className="date-field">
      <input id={id} type="text" inputMode="numeric" autoComplete="off" placeholder="YYYY-MM-DD" maxLength={10} value={text} disabled={disabled} required={required}
        aria-label={id ? undefined : label} aria-invalid={invalid || undefined} title={invalid ? `${min.slice(0, 4)}~${max.slice(0, 4)}년 사이의 날짜를 YYYY-MM-DD 로 입력하세요` : undefined}
        onChange={(e) => commit(formatDateDigits(e.target.value))} onBlur={() => setTouched(true)} />
      <button type="button" className="date-field-btn" disabled={disabled} aria-label="달력에서 고르기" title={`${label ?? '날짜'} 달력에서 고르기`}
        onClick={() => { const el = picker.current; if (!el) return; try { el.showPicker(); } catch { el.focus(); } }}>
        <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" />
        </svg>
      </button>
      {/* 달력 팝업용(보이지 않음). 고른 값은 위 칸으로 들어간다 */}
      <input ref={picker} className="date-field-picker" type="date" tabIndex={-1} aria-hidden="true" min={min} max={max} value={isRealDate(value) ? value : ''} disabled={disabled}
        onChange={(e) => { if (e.target.value) commit(e.target.value); }} />
      {invalid && <span className="date-field-error" role="alert">YYYY-MM-DD 로 입력하세요</span>}
    </span>
  );
}
